import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Modal,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  OutlinedInput,
  CircularProgress,
  Alert,
  Grid,
  FormHelperText,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  InputAdornment,
  Tooltip,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";

import {
  createBudgetRecord,
  updateBudgetRecord,
} from "../../../store/spendCategorization/budgetSlice";
import { shouldFilterUsername } from "../../../helpers/util_helper";

const BudgetRecordModal = ({
  open,
  onClose,
  record,
  resourceConfigs,
  isFetchingResourceConfigs,
  orgBudgets,
}) => {
  const dispatch = useDispatch();
  const { isCreatingBudgetRecord, isUpdatingBudgetRecord } = useSelector(
    (state) => state.budget
  );

  const [formData, setFormData] = useState({
    create_new_rg: true,
    resource_group_name: "",
    resource_group_description: "",
    accounts: [],
    users: [],
    budget: {
      amount: "",
      currency: "USD",
      start_date: "",
      end_date: "",
      admin_name: "",
      admin_email: "",
      admin_phone: "",
    },
  });

  // Track original accounts for edit mode
  const [originalAccounts, setOriginalAccounts] = useState([]);
  const [originalData, setOriginalData] = useState(null);

  const [errors, setErrors] = useState({});
  const [formTouched, setFormTouched] = useState(false);
  const [touchedFields, setTouchedFields] = useState({});

  // Check for duplicate resource group names
  const checkDuplicateResourceGroup = (name) => {
    if (!name || !resourceConfigs?.resource_groups) return false;
    const trimmedName = name.trim();
    return resourceConfigs.resource_groups.some(
      (group) => group.trim() === trimmedName
    );
  };

  // Get remaining funds from active org budget
  const getRemainingFunds = () => {
    if (!orgBudgets || orgBudgets.length === 0) return null;
    
    const activeBudgets = orgBudgets.filter(
      (budget) => budget.status === "ACTIVE"
    );
    
    if (activeBudgets.length === 0) return null;
    
    return {
      amount: activeBudgets[0].remaining_amount || 0,
      currency: activeBudgets[0].currency || "USD",
      percentage: activeBudgets[0].remaining_pct || 0,
    };
  };

  // Format currency helper
  const formatCurrency = (amount, currency = "USD") => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency,
    }).format(amount);
  };

  // Check if a specific field should show an error
  const shouldShowFieldError = (fieldName) => {
    // Show error if field has been touched or form has been submitted
    return touchedFields[fieldName] || formTouched;
  };

  // Get error message for a specific field
  const getFieldError = useMemo(() => {
    return (fieldName) => {
      if (!shouldShowFieldError(fieldName)) return null;
      
      // Check for validation errors based on current form data
      if (!record) {
        // Resource group validation
        if (formData.create_new_rg) {
          if (fieldName === 'resource_group_name' && !formData.resource_group_name?.trim()) {
            return "Budget group name is required";
          }
          if (fieldName === 'resource_group_description' && !formData.resource_group_description?.trim()) {
            return "Budget group description is required";
          }
        } else {
          if (fieldName === 'resource_group_name' && !formData.resource_group_name?.trim()) {
            return "Please select a budget group";
          }
        }
      }

      // Budget validation
      if (fieldName === 'budget.amount' && (!formData.budget.amount || parseFloat(formData.budget.amount) <= 0)) {
        return "Valid budget amount is required";
      }
      
      // Check if budget amount exceeds remaining funds
      if (fieldName === 'budget.amount' && formData.budget.amount) {
        const remainingFunds = getRemainingFunds();
        if (remainingFunds) {
          const newAmount = parseFloat(formData.budget.amount);
          
          if (record) {
            // For UPDATES: Check if the INCREASE exceeds remaining funds
            const oldAmount = parseFloat(record.amount) || 0;
            const increase = newAmount - oldAmount;
            
            if (increase > 0 && increase > remainingFunds.amount) {
              return `Budget increase (${formatCurrency(increase, remainingFunds.currency)}) exceeds available funds (${formatCurrency(remainingFunds.amount, remainingFunds.currency)} remaining)`;
            }
          } else {
            // For NEW budgets: Check if amount exceeds remaining funds
            if (newAmount > remainingFunds.amount) {
              return `Budget amount exceeds available funds (${formatCurrency(remainingFunds.amount, remainingFunds.currency)} remaining)`;
            }
          }
        }
      }
      if (fieldName === 'budget.start_date' && !formData.budget.start_date) {
        return "Start date is required";
      }
      if (fieldName === 'budget.end_date' && !formData.budget.end_date) {
        return "End date is required";
      }
      if (fieldName === 'budget.admin_name' && !formData.budget.admin_name?.trim()) {
        return "Name is required";
      }
      if (fieldName === 'budget.admin_email' && !formData.budget.admin_email?.trim()) {
        return "Email is required";
      }

      // Accounts validation
      if (fieldName === 'accounts' && formData.accounts.length === 0) {
        return "At least one account is required";
      }

      // Users validation (only for new records)
      if (!record && fieldName === 'users' && formData.users.length === 0) {
        return "At least one user is required";
      }

      return null;
    };
  }, [formData, record, touchedFields, formTouched]);

  // Memoize form validation to prevent infinite re-renders
  const isFormValid = useMemo(() => {
    // Don't validate at all if modal is not open
    if (!open) {
      return true;
    }

    // Check if all required fields have values (for submit button state)
    const hasRequiredValues = () => {
      // Resource group validation
      if (!record) {
        if (formData.create_new_rg) {
          if (!formData.resource_group_name?.trim() || !formData.resource_group_description?.trim()) {
            return false;
          }
        } else {
          if (!formData.resource_group_name?.trim()) {
            return false;
          }
        }
      }

      // Budget validation
      if (!formData.budget.amount || parseFloat(formData.budget.amount) <= 0) {
        return false;
      }
      
      // Check if budget amount exceeds remaining funds
      const remainingFunds = getRemainingFunds();
      if (remainingFunds) {
        const newAmount = parseFloat(formData.budget.amount);
        
        if (record) {
          // For UPDATES: Check if the INCREASE exceeds remaining funds
          // If reducing budget, always allow (no need to check)
          const oldAmount = parseFloat(record.amount) || 0;
          const increase = newAmount - oldAmount;
          
          if (increase > 0 && increase > remainingFunds.amount) {
            return false; // Increase exceeds remaining funds
          }
        } else {
          // For NEW budgets: Check if amount exceeds remaining funds
          if (newAmount > remainingFunds.amount) {
            return false;
          }
        }
      }
      if (!formData.budget.start_date || !formData.budget.end_date) {
        return false;
      }
      if (!formData.budget.admin_name?.trim() || !formData.budget.admin_email?.trim()) {
        return false;
      }

      // Accounts validation
      if (formData.accounts.length === 0) {
        return false;
      }

      // Users validation (only for new records)
      if (!record && formData.users.length === 0) {
        return false;
      }

      return true;
    };

    return hasRequiredValues();
  }, [formData, open, record]);

  // Update errors when form validation changes
  useEffect(() => {
    if (!open) {
      if (Object.keys(errors).length > 0) {
        setErrors({});
      }
      return;
    }

    // Only show validation errors after user has attempted to submit or interacted with fields
    if (!formTouched) {
      return;
    }

    const newErrors = {};

    // Only validate budget group fields if not in edit mode
    if (!record) {
      // Budget group validation
      if (formData.create_new_rg) {
        if (shouldShowFieldError('resource_group_name') && !formData.resource_group_name?.trim()) {
          newErrors.resource_group_name = "Budget group name is required";
        } else if (touchedFields.resource_group_name && checkDuplicateResourceGroup(formData.resource_group_name)) {
          newErrors.resource_group_name =
            "Budget group with this name already exists";
        }
        if (shouldShowFieldError('resource_group_description') && !formData.resource_group_description?.trim()) {
          newErrors.resource_group_description =
            "Budget group description is required";
        }
      } else {
        if (shouldShowFieldError('resource_group_name') && !formData.resource_group_name?.trim()) {
          newErrors.resource_group_name = "Please select a budget group";
        }
      }
    }

    // Budget amount validation (always required)
    if (shouldShowFieldError('budget.amount') && (!formData.budget.amount || parseFloat(formData.budget.amount) <= 0)) {
      newErrors.amount = "Valid budget amount is required";
    }

    // Date validation (always required)
    if (shouldShowFieldError('budget.start_date') && !formData.budget.start_date) {
      newErrors.start_date = "Start date is required";
    }

    if (shouldShowFieldError('budget.end_date') && !formData.budget.end_date) {
      newErrors.end_date = "End date is required";
    }

    if (formData.budget.start_date && formData.budget.end_date) {
      const startDate = new Date(formData.budget.start_date);
      const endDate = new Date(formData.budget.end_date);
      if (endDate <= startDate) {
        newErrors.end_date = "End date must be after start date";
      }
    }

    // Admin validation (always required)
    if (shouldShowFieldError('budget.admin_name') && !formData.budget.admin_name?.trim()) {
      newErrors.admin_name = "Name is required";
    }

    if (shouldShowFieldError('budget.admin_email') && !formData.budget.admin_email?.trim()) {
      newErrors.admin_email = "Email is required";
    } else if (touchedFields['budget.admin_email'] && formData.budget.admin_email && !/\S+@\S+\.\S+/.test(formData.budget.admin_email)) {
      newErrors.admin_email = "Valid email is required";
    }

    // Validate accounts for both new and edit modes
    if (shouldShowFieldError('accounts') && formData.accounts.length === 0) {
      newErrors.accounts = "At least one account is required";
    }

    // Only validate users if not in edit mode
    if (!record) {
      if (shouldShowFieldError('users') && formData.users.length === 0) {
        newErrors.users = "At least one user is required";
      }
    }

    setErrors(newErrors);
  }, [formData, open, formTouched, record, touchedFields]);

  // Check if form has changes (only for edit mode)
  const hasChanges = useMemo(() => {
    if (!originalData || !record) return false; // No changes for new record creation

    return (
      formData.budget.amount !== originalData.budget.amount ||
      formData.budget.start_date !== originalData.budget.start_date ||
      formData.budget.end_date !== originalData.budget.end_date ||
      formData.budget.admin_name !== originalData.budget.admin_name ||
      formData.budget.admin_email !== originalData.budget.admin_email ||
      formData.budget.admin_phone !== originalData.budget.admin_phone ||
      JSON.stringify(formData.accounts.sort()) !==
        JSON.stringify(originalData.accounts.sort())
    );
  }, [formData, originalData, record]);

  // Initialize form data when editing
  useEffect(() => {
    if (record) {
      const recordAccounts = record.accounts
        ? record.accounts.map((acc) => acc.accnumber)
        : [];
      const newFormData = {
        create_new_rg: false, // Always false for editing existing records
        resource_group_name: record.resource_group_name || "",
        resource_group_description: "",
        accounts: recordAccounts,
        users: [],
        budget: {
          amount: record.amount || "",
          currency: record.currency || "USD",
          start_date: record.start_date || "",
          end_date: record.end_date || "",
          admin_name: record.admin_name || "",
          admin_email: record.admin_email || "",
          admin_phone: record.admin_phone || "",
        },
      };
      setFormData(newFormData);
      setOriginalData(newFormData); // Store original data for change detection
      setOriginalAccounts(recordAccounts);
    } else {
      // Reset form for new record
      const emptyFormData = {
        create_new_rg: true,
        resource_group_name: "",
        resource_group_description: "",
        accounts: [],
        users: [],
        budget: {
          amount: "",
          currency: "USD",
          start_date: "",
          end_date: "",
          admin_name: "",
          admin_email: "",
          admin_phone: "",
        },
      };
      setFormData(emptyFormData);
      setOriginalData(null);
      setOriginalAccounts([]);
    }
    setErrors({});
    setFormTouched(false);
    setTouchedFields({});
  }, [record, open]);

  const handleChange = (field, value) => {
    if (field.includes(".")) {
      const [parent, child] = field.split(".");
      setFormData((prev) => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
    // Clear error for this field
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const handleBlur = (field) => {
    // Mark this specific field as touched when user leaves it
    setTouchedFields((prev) => ({ ...prev, [field]: true }));
  };

  const handleResourceGroupTypeChange = (value) => {
    const createNewRG = value === "new";
    setFormData((prev) => ({
      ...prev,
      create_new_rg: createNewRG,
      resource_group_name: createNewRG ? "" : prev.resource_group_name,
      resource_group_description: createNewRG ? "" : "",
    }));
    // Clear errors for resource group fields when switching types
    if (errors.resource_group_name) {
      setErrors((prev) => ({ ...prev, resource_group_name: null }));
    }
    if (errors.resource_group_description) {
      setErrors((prev) => ({ ...prev, resource_group_description: null }));
    }
  };

  const handleMultiSelectChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  // Calculate accounts to add and remove for edit mode
  const calculateAccountChanges = () => {
    if (!record) return { to_add: [], to_remove: [] };

    const currentAccounts = new Set(formData.accounts);
    const originalAccountsSet = new Set(originalAccounts);

    const to_add = formData.accounts.filter(
      (acc) => !originalAccountsSet.has(acc)
    );
    const to_remove = originalAccounts.filter(
      (acc) => !currentAccounts.has(acc)
    );

    // Debug logging
    console.log("🔍 Account Changes Calculation:");
    console.log("Original accounts:", originalAccounts);
    console.log("Current accounts:", formData.accounts);
    console.log("To add:", to_add);
    console.log("To remove:", to_remove);

    return { to_add, to_remove };
  };

  const handleSubmit = () => {
    // Mark form as touched when user attempts to submit
    if (!formTouched) {
      setFormTouched(true);
    }
    
    // Mark all fields as touched when attempting to submit
    const allFields = [
      'resource_group_name',
      'resource_group_description', 
      'accounts',
      'users',
      'budget.amount',
      'budget.start_date',
      'budget.end_date',
      'budget.admin_name',
      'budget.admin_email',
      'budget.admin_phone'
    ];
    
    const newTouchedFields = {};
    allFields.forEach(field => {
      newTouchedFields[field] = true;
    });
    setTouchedFields(newTouchedFields);
    
    if (!isFormValid) return;

    if (record) {
      // Update existing record - include account changes
      const { to_add, to_remove } = calculateAccountChanges();
      const updatePayload = {
        budgetId: record.budget_id,
        amount: parseFloat(formData.budget.amount),
        currency: formData.budget.currency,
        start_date: formData.budget.start_date,
        end_date: formData.budget.end_date,
        admin_name: formData.budget.admin_name,
        admin_email: formData.budget.admin_email,
        admin_phone: formData.budget.admin_phone,
        to_add,
        to_remove,
      };

      // Debug logging
      console.log("📤 Update Payload:", updatePayload);

      dispatch(updateBudgetRecord(updatePayload));
    } else {
      // Create new record - send full payload with trimmed values
      const payload = {
        ...formData,
        resource_group_name: formData.resource_group_name?.trim(),
        resource_group_description: formData.resource_group_description?.trim(),
        budget: {
          ...formData.budget,
          amount: parseFloat(formData.budget.amount),
          admin_name: formData.budget.admin_name?.trim(),
          admin_email: formData.budget.admin_email?.trim(),
          admin_phone: formData.budget.admin_phone?.trim(),
        },
      };

      // Debug logging
      console.log("📤 Create Payload:", payload);

      dispatch(createBudgetRecord(payload));
    }
  };

  const handleClose = () => {
    setFormData({
      create_new_rg: true,
      resource_group_name: "",
      resource_group_description: "",
      accounts: [],
      users: [],
      budget: {
        amount: "",
        currency: "USD",
        start_date: "",
        end_date: "",
        admin_name: "",
        admin_email: "",
        admin_phone: "",
      },
    });
    setOriginalAccounts([]);
    setErrors({});
    setFormTouched(false);
    setTouchedFields({});

    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="budget-modal-title"
      aria-describedby="budget-modal-description"
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "90%",
          maxWidth: 800,
          maxHeight: "90vh",
          overflow: "auto",
          backgroundColor: "white",
          borderRadius: "12px",
          boxShadow:
            "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
          p: 0,
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 3,
            borderBottom: "1px solid #e2e8f0",
            backgroundColor: "#f8fafc",
            borderTopLeftRadius: "12px",
            borderTopRightRadius: "12px",
          }}
        >
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              color: "#1e293b",
              fontSize: "1.25rem",
            }}
          >
            {record ? "Edit Budget Group" : "Create Budget Group"}
          </Typography>
          {record && (
            <Typography
              variant="body2"
              sx={{
                color: "#64748b",
                fontSize: "0.875rem",
                mt: 1,
                fontStyle: "italic",
              }}
            >
              You can edit budget details, contact information, and manage
              accounts
            </Typography>
          )}
        </Box>

        {/* Content */}
        <Box sx={{ p: 3 }}>
          {isFetchingResourceConfigs && (
            <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
              <CircularProgress size={24} sx={{ color: "#3b82f6" }} />
              <Typography
                sx={{ ml: 2, color: "#64748b", fontSize: "0.875rem" }}
              >
                Loading resource configurations...
              </Typography>
            </Box>
          )}

          <Grid container spacing={2}>
            {/* Resource Group Section - Only show for new records */}
            {!record && (
              <>
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      mb: 1.5,
                      pb: 0.5,
                      borderBottom: "1px solid #f1f5f9",
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        color: "#1e293b",
                        fontSize: "1rem",
                      }}
                    >
                      Budget Group
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <FormControl component="fieldset" sx={{ mb: 2 }}>
                    <RadioGroup
                      row
                      value={formData.create_new_rg ? "new" : "existing"}
                      onChange={(e) =>
                        handleResourceGroupTypeChange(e.target.value)
                      }
                      disabled={isFetchingResourceConfigs}
                      sx={{ gap: 3 }}
                    >
                      <FormControlLabel
                        value="new"
                        control={
                          <Radio size="small" sx={{ color: "#3b82f6" }} />
                        }
                        label={
                          <Typography
                            sx={{
                              fontSize: "0.875rem",
                              color: "#374151",
                              fontWeight: 500,
                            }}
                          >
                            Create New Budget Group
                          </Typography>
                        }
                      />
                      <FormControlLabel
                        value="existing"
                        control={
                          <Radio size="small" sx={{ color: "#3b82f6" }} />
                        }
                        label={
                          <Typography
                            sx={{
                              fontSize: "0.875rem",
                              color: "#374151",
                              fontWeight: 500,
                            }}
                          >
                            Select Existing Budget Group
                          </Typography>
                        }
                      />
                    </RadioGroup>
                  </FormControl>
                </Grid>

                {formData.create_new_rg ? (
                  // Create New Resource Group
                  <>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="New Budget Group Name *"
                        value={formData.resource_group_name}
                        onChange={(e) =>
                          handleChange("resource_group_name", e.target.value)
                        }
                        onBlur={() => {
                          handleBlur("resource_group_name");
                          // Check for duplicates when user finishes typing
                          if (
                            formData.resource_group_name?.trim() &&
                            checkDuplicateResourceGroup(
                              formData.resource_group_name
                            )
                          ) {
                            setErrors((prev) => ({
                              ...prev,
                              resource_group_name:
                                "Budget group with this name already exists",
                            }));
                          }
                        }}
                        error={!!getFieldError("resource_group_name")}
                        helperText={getFieldError("resource_group_name")}
                        disabled={isFetchingResourceConfigs}
                        placeholder="Enter budget group name"
                        sx={{
                          "& .MuiInputBase-root": {
                            backgroundColor: "white",
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Budget Group Description *"
                        value={formData.resource_group_description}
                        onChange={(e) =>
                          handleChange(
                            "resource_group_description",
                            e.target.value
                          )
                        }
                        onBlur={() => handleBlur("resource_group_description")}
                        error={!!getFieldError("resource_group_description")}
                        helperText={getFieldError("resource_group_description")}
                        disabled={isFetchingResourceConfigs}
                        placeholder="Enter description for the new budget group"
                        sx={{
                          "& .MuiInputBase-root": {
                            backgroundColor: "white",
                          },
                        }}
                      />
                    </Grid>
                  </>
                ) : (
                  // Select Existing Resource Group
                  <Grid item xs={12}>
                    <FormControl fullWidth error={!!getFieldError("resource_group_name")}>
                      <InputLabel>Select Budget Group *</InputLabel>
                      <Select
                        value={formData.resource_group_name}
                        onChange={(e) =>
                          handleChange("resource_group_name", e.target.value)
                        }
                        onBlur={() => handleBlur("resource_group_name")}
                        label="Select Budget Group *"
                        disabled={isFetchingResourceConfigs}
                        MenuProps={{
                          PaperProps: {
                            style: {
                              maxHeight: 300,
                            },
                          },
                        }}
                        sx={{
                          "& .MuiInputBase-root": {
                            backgroundColor: "white",
                          },
                        }}
                      >
                        {resourceConfigs?.resource_groups
                          ?.filter((group) => group !== "Default")
                          .map((group) => (
                            <MenuItem key={group} value={group}>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 500 }}
                              >
                                {group}
                              </Typography>
                            </MenuItem>
                          ))}
                      </Select>
                      {getFieldError("resource_group_name") && (
                        <FormHelperText>
                          {getFieldError("resource_group_name")}
                        </FormHelperText>
                      )}
                    </FormControl>
                  </Grid>
                )}
              </>
            )}

            {/* Accounts Section - Show for both new and edit modes */}
            <Grid item xs={12}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  mb: 1.5,
                  pb: 0.5,
                  borderBottom: "1px solid #f1f5f9",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 600, color: "#1e293b", fontSize: "1rem" }}
                >
                  {record ? "Manage Accounts" : "Access Control"}
                </Typography>
              </Box>
              {!record && (
                <Typography
                  variant="body2"
                  sx={{
                    color: "#64748b",
                    fontSize: "0.75rem",
                    mb: 1.5,
                    fontStyle: "italic",
                    lineHeight: 1.4,
                  }}
                >
                  Add the AWS accounts that will be part of this budget group. Select the list of users who can access the budget and spend details of these accounts
                </Typography>
              )}
              {record && (
                <Typography
                  variant="body2"
                  sx={{
                    color: "#64748b",
                    fontSize: "0.8rem",
                    mb: 1.5,
                    fontStyle: "italic",
                  }}
                >
                  Add or remove accounts from this budget group. Selected
                  accounts are highlighted. Changes will be applied when you
                  save.
                </Typography>
              )}
            </Grid>

            <Grid item xs={12} md={record ? 12 : 6}>
              <FormControl fullWidth error={!!getFieldError("accounts")}>
                <InputLabel>Select Accounts *</InputLabel>
                <Select
                  multiple
                  value={formData.accounts}
                  onChange={(e) =>
                    handleMultiSelectChange("accounts", e.target.value)
                  }
                  onBlur={() => handleBlur("accounts")}
                  input={<OutlinedInput label="Select Accounts *" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {selected.map((value) => {
                        // First try to find in resource config accounts
                        let account = resourceConfigs?.accounts?.find(
                          (acc) => acc.accnumber === value
                        );

                        // If not found in resource config, try to find in budget accounts (for edit mode)
                        if (!account && record && record.accounts) {
                          account = record.accounts.find(
                            (acc) => acc.accnumber === value
                          );
                        }

                        return (
                          <Chip
                            key={value}
                            label={
                              account
                                ? `${
                                    account.accname || account.accnumber
                                  } (${value})`
                                : value
                            }
                            size="small"
                          />
                        );
                      })}
                    </Box>
                  )}
                  disabled={isFetchingResourceConfigs}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 300,
                      },
                    },
                    sx: {
                      "& .MuiMenuItem-root[data-status='no-status'], .MuiMenuItem-root[data-status='shadow']":
                        {
                          backgroundColor: "#f3f4f6",
                          "&:hover": {
                            backgroundColor: "#e5e7eb",
                          },
                          "&.selected": {
                            backgroundColor: "#e0f2fe",
                            borderLeft: "3px solid #3b82f6",
                          },
                        },
                      "& .MuiMenuItem-root[data-status='onboarded']": {
                        backgroundColor: "#ffffff",
                        "&:hover": {
                          backgroundColor: "#f9fafb",
                        },
                        "&.selected": {
                          backgroundColor: "#f0f9ff",
                          borderLeft: "3px solid #3b82f6",
                        },
                      },
                    },
                  }}
                  sx={{
                    "& .MuiInputBase-root": {
                      backgroundColor: "white",
                    },
                  }}
                >
                  {(() => {
                    // Get all accounts from resource config
                    const resourceAccounts = resourceConfigs?.accounts || [];

                    // In edit mode, also include accounts from the budget that might not be in resource config
                    let allAccounts = [...resourceAccounts];

                    if (record && record.accounts) {
                      const budgetAccounts = record.accounts.map((acc) => ({
                        accnumber: acc.accnumber,
                        accname: acc.accname || acc.accnumber,
                        status: acc.status || "No Status",
                      }));

                      // Add budget accounts that are not already in resource accounts
                      budgetAccounts.forEach((budgetAcc) => {
                        if (
                          !resourceAccounts.some(
                            (resourceAcc) =>
                              resourceAcc.accnumber === budgetAcc.accnumber
                          )
                        ) {
                          allAccounts.push(budgetAcc);
                        }
                      });
                    }

                    return allAccounts.map((account) => {
                      const status = account.status || "No Status";
                      const statusClass = status
                        .toLowerCase()
                        .replace(/\s+/g, "-");

                      // Check if this account is currently selected
                      const isSelected = formData.accounts.includes(
                        account.accnumber
                      );

                      return (
                        <MenuItem
                          key={account.accnumber}
                          value={account.accnumber}
                          data-status={statusClass}
                          sx={{
                            backgroundColor: isSelected
                              ? "#f0f9ff"
                              : "transparent",
                            borderLeft: isSelected
                              ? "3px solid #3b82f6"
                              : "3px solid transparent",
                            "&:hover": {
                              backgroundColor: isSelected
                                ? "#e0f2fe"
                                : "#f8fafc",
                            },
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              width: "100%",
                            }}
                          >
                            <Box>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: isSelected ? 600 : 500,
                                  color: isSelected ? "#1e40af" : "#374151",
                                }}
                              >
                                {account.accname}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: isSelected ? "#3b82f6" : "#666",
                                  fontWeight: isSelected ? 500 : 400,
                                }}
                              >
                                {account.accnumber + (account.status === "ONBOARDED" ? " - ONBOARDED" : "")}
                              </Typography>
                            </Box>
                            {isSelected && (
                              <Box
                                sx={{
                                  width: 20,
                                  height: 20,
                                  borderRadius: "50%",
                                  backgroundColor: "#3b82f6",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  color: "white",
                                  fontSize: "12px",
                                }}
                              >
                                ✓
                              </Box>
                            )}
                          </Box>
                        </MenuItem>
                      );
                    });
                  })()}
                </Select>
                {getFieldError("accounts") && (
                  <FormHelperText>{getFieldError("accounts")}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            {/* Users Section - Only show for new records */}
            {!record && (
              <Grid item xs={12} md={6}>
                <FormControl fullWidth error={!!getFieldError("users")}>
                  <InputLabel>Select Users *</InputLabel>
                  <Select
                    multiple
                    value={formData.users}
                    onChange={(e) =>
                      handleMultiSelectChange("users", e.target.value)
                    }
                    onBlur={() => handleBlur("users")}
                    input={<OutlinedInput label="Select Users *" />}
                    renderValue={(selected) => (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip key={value} label={value} size="small" />
                        ))}
                      </Box>
                    )}
                    disabled={isFetchingResourceConfigs}
                    MenuProps={{
                      PaperProps: {
                        style: {
                          maxHeight: 300,
                        },
                      },
                    }}
                    sx={{
                      "& .MuiInputBase-root": {
                        backgroundColor: "white",
                      },
                    }}
                  >
                    {resourceConfigs?.users
                      ?.filter((user) => !shouldFilterUsername(user.email))
                      ?.map((user) => (
                        <MenuItem key={user.email} value={user.email}>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {user.email}
                            </Typography>
                            <Typography variant="caption" sx={{ color: "#666" }}>
                              Budget Groups: {user.resource_groups.join(", ")}
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))}
                  </Select>
                  {getFieldError("users") && (
                    <FormHelperText>{getFieldError("users")}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
            )}

            {/* Budget Section */}
            <Grid item xs={12}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  mb: 1.5,
                  pb: 0.5,
                  borderBottom: "1px solid #f1f5f9",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 600, color: "#1e293b", fontSize: "1rem" }}
                >
                  Budget Details
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Budget Amount *"
                type="number"
                value={formData.budget.amount}
                onChange={(e) => handleChange("budget.amount", e.target.value)}
                onBlur={() => handleBlur("budget.amount")}
                error={!!getFieldError("budget.amount")}
                helperText={getFieldError("budget.amount")}
                inputProps={{ min: 0, step: 0.01 }}
                InputProps={{
                  endAdornment: (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        px: 1.5,
                        py: 0.5,
                        backgroundColor: "#f3f4f6",
                        borderRadius: "4px",
                        border: "1px solid #d1d5db",
                        ml: 1,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "0.75rem",
                          fontWeight: 600,
                          color: "#374151",
                          fontFamily: "monospace",
                        }}
                      >
                        USD
                      </Typography>
                    </Box>
                  ),
                }}
              />
              {/* Remaining Funds Display */}
              {(() => {
                const remainingFunds = getRemainingFunds();
                if (remainingFunds) {
                  return (
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#64748b",
                        fontSize: "0.75rem",
                        fontStyle: "italic",
                        display: "block",
                        mt: 0.5,
                      }}
                    >
                      Remaining Organization Funds: {formatCurrency(remainingFunds.amount, remainingFunds.currency)}
                    </Typography>
                  );
                }
                return null;
              })()}
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Start Date *"
                type="date"
                value={formData.budget.start_date}
                onChange={(e) =>
                  handleChange("budget.start_date", e.target.value)
                }
                onBlur={() => handleBlur("budget.start_date")}
                error={!!getFieldError("budget.start_date")}
                helperText={getFieldError("budget.start_date")}
                InputLabelProps={{
                  shrink: true,
                }}
                // Allow any past date - no restrictions
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="End Date *"
                type="date"
                value={formData.budget.end_date}
                onChange={(e) =>
                  handleChange("budget.end_date", e.target.value)
                }
                onBlur={() => handleBlur("budget.end_date")}
                error={!!getFieldError("budget.end_date")}
                helperText={getFieldError("budget.end_date")}
                inputProps={{
                  min: formData.budget.start_date
                    ? new Date(
                        new Date(formData.budget.start_date).getTime() +
                          24 * 60 * 60 * 1000
                      )
                        .toISOString()
                        .split("T")[0]
                    : undefined,
                }}
                InputLabelProps={{
                  shrink: true,
                }}
                // End date must be at least 1 day after start date
              />
            </Grid>

            {/* Contact Section */}
            <Grid item xs={12}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  mb: 1.5,
                  pb: 0.5,
                  borderBottom: "1px solid #f1f5f9",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 600, color: "#1e293b", fontSize: "1rem" }}
                >
                  Contact
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Name *"
                value={formData.budget.admin_name}
                onChange={(e) =>
                  handleChange("budget.admin_name", e.target.value)
                }
                onBlur={() => handleBlur("budget.admin_name")}
                error={!!getFieldError("budget.admin_name")}
                helperText={getFieldError("budget.admin_name")}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth error={!!getFieldError("budget.admin_email")}>
                <InputLabel>Email *</InputLabel>
                <Select
                  value={formData.budget.admin_email}
                  onChange={(e) =>
                    handleChange("budget.admin_email", e.target.value)
                  }
                  onBlur={() => handleBlur("budget.admin_email")}
                  label="Email *"
                  disabled={isFetchingResourceConfigs}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 300,
                      },
                    },
                  }}
                  sx={{
                    "& .MuiInputBase-root": {
                      backgroundColor: "white",
                    },
                  }}
                >
                  {/* Show current user email if it's not in the resourceConfigs.users list */}
                  {record &&
                    formData.budget.admin_email &&
                    !resourceConfigs?.users?.some(
                      (user) => user.email === formData.budget.admin_email
                    ) && (
                      <MenuItem
                        key={formData.budget.admin_email}
                        value={formData.budget.admin_email}
                      >
                        <Box sx={{ display: "flex", flexDirection: "column" }}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {formData.budget.admin_email}
                          </Typography>
                        </Box>
                      </MenuItem>
                    )}
                  {resourceConfigs?.users
                    ?.filter((user) => !shouldFilterUsername(user.email))
                    ?.map((user) => (
                      <MenuItem key={user.email} value={user.email}>
                        <Box sx={{ display: "flex", flexDirection: "column" }}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {user.email}
                          </Typography>
                          {user.name && (
                            <Typography
                              variant="caption"
                              sx={{ color: "#64748b" }}
                            >
                              {user.name}
                            </Typography>
                          )}
                        </Box>
                      </MenuItem>
                    ))}
                </Select>
                {getFieldError("budget.admin_email") && (
                  <FormHelperText>{getFieldError("budget.admin_email")}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Phone *"
                value={formData.budget.admin_phone}
                onChange={(e) =>
                  handleChange("budget.admin_phone", e.target.value)
                }
                onBlur={() => handleBlur("budget.admin_phone")}
                error={!!getFieldError("budget.admin_phone")}
                helperText={getFieldError("budget.admin_phone")}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Footer */}
        <Box
          sx={{
            p: 3,
            borderTop: "1px solid #e2e8f0",
            backgroundColor: "#f8fafc",
            borderBottomLeftRadius: "12px",
            borderBottomRightRadius: "12px",
            display: "flex",
            justifyContent: "flex-end",
            gap: 2,
          }}
        >
          <Button
            variant="outlined"
            onClick={handleClose}
            disabled={isCreatingBudgetRecord || isUpdatingBudgetRecord}
            sx={{
              borderColor: "#d1d5db",
              color: "#374151",
              "&:hover": {
                borderColor: "#9ca3af",
                backgroundColor: "#f9fafb",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={
              isCreatingBudgetRecord ||
              isUpdatingBudgetRecord ||
              isFetchingResourceConfigs ||
              !isFormValid ||
              (record && !hasChanges)
            }
            sx={{
              backgroundColor: "#3b82f6",
              color: "white",
              fontWeight: 600,
              "&:hover": {
                backgroundColor: "#2563eb",
              },
              "&:disabled": {
                backgroundColor: "#9ca3af",
              },
            }}
          >
            {isCreatingBudgetRecord || isUpdatingBudgetRecord ? (
              <>
                <CircularProgress size={16} sx={{ color: "white", mr: 1 }} />
                {record ? "Updating..." : "Creating..."}
              </>
            ) : record ? (
              "Update Budget Group"
            ) : (
              "Create Budget Group"
            )}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default BudgetRecordModal;
